import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { useApiQuery, type Product } from 'grabbitbot-legacy-ui'
import { paymentClient } from '../../api'
import { usePaymentActions, usePaymentBasics } from './paymentFlow'
import { PaymentPage, ProductCard } from './paymentShared'
import { failureText } from '../../api/errorMessages'

/**
 * `/payment/products[/:productId]` — legacy `ProductListComponent`: every
 * one-time product with its paid state and pay actions. With `:productId`
 * the product is shown first and highlighted; one missing from the list is
 * fetched on its own (`GET /payment/products/{id}`), as legacy did.
 */
export function ProductListPage() {
  const { productId } = useParams<{ productId?: string }>()
  const basics = usePaymentBasics()
  const { generation, refetch, method, billing } = basics
  const actions = usePaymentActions()
  const products = useApiQuery(() => paymentClient.listProducts(), [generation])
  const list = Array.isArray(products.data) ? products.data : null
  const [extra, setExtra] = useState<Product | null>(null)
  const [extraError, setExtraError] = useState<unknown>(null)

  const inList = Boolean(productId && list?.some((p) => p.id === productId))
  useEffect(() => {
    setExtra(null)
    setExtraError(null)
    if (!productId || !list || inList) return
    let cancelled = false
    paymentClient
      .viewProduct(productId)
      .then((product) => {
        if (!cancelled) setExtra(product && typeof product === 'object' ? product : null)
      })
      .catch((err: unknown) => {
        if (!cancelled) setExtraError(err ?? true)
      })
    return () => {
      cancelled = true
    }
  }, [productId, list, inList])

  const all = [...(list ?? []), ...(extra ? [extra] : [])]
  const ordered = productId ? [...all.filter((p) => p.id === productId), ...all.filter((p) => p.id !== productId)] : all

  return (
    <PaymentPage title="Products">
      {products.loading && <p>Loading products…</p>}
      {products.error && (
        <p className="portal-notice portal-notice--error" role="alert">
          {failureText(products.error, 'Could not load products')}
        </p>
      )}
      {extraError !== null && (
        <p className="portal-notice portal-notice--warning" role="alert">
          No such product.
        </p>
      )}
      {list && ordered.length === 0 && <p className="portal-text-muted">No products.</p>}
      <div className="portal-grid-2">
        {ordered.map((product) => (
          <ProductCard
            key={product.id ?? product.name}
            product={product}
            method={method}
            billing={billing}
            actions={actions}
            onChanged={refetch}
            highlighted={Boolean(productId && product.id === productId)}
          />
        ))}
      </div>
    </PaymentPage>
  )
}
